import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../../../../services/admin.service';
import { ReportType, ReportTypeWithVersions } from '../../../../configs/model/reports.model';
import { convertDateToString, convertISO8601IntervalToPostgres, convertMonthNameYearToISO8601, convertMonthYearToISO8601, convertQuarterYearToISO8601, deepSort, finraGridPercentMultipliedValueFormatter, finraGridPercentValueFormatter, getMonthYrDate, getPrior12Quarters, getPrior36Months, PriorMonthsList, PriorQuartersList, quarterDateFormat, saveCsv, trimDateTimeToDate, trimTrailingChar } from '../../../../shared/utils/utils';
import { AuditStatsService } from 'src/app/services/audit-stats.service';
import { UiWidgetsService } from 'src/app/services/ui-widgets.service';
import { HttpErrorResponse } from '@angular/common/http';
import JSZip from 'jszip';

@Component({
  selector: 'audit-stats-industry',
  templateUrl: './audit-stats-industry.component.html',
  styleUrls: ['./audit-stats-industry.component.scss']
})
export class AuditStatsIndustryComponent implements OnInit {

  auditStatsViews = [
    { name: 'Industry', value: 'industry' },
    { name: 'Firm', value: 'firm' },
  ];

  currentView = this.auditStatsViews[0];

  auditSearchFormGroup: FormGroup;
  submittedFormValues: any;
  showGrid = false;
  reportTypesList: ReportTypeWithVersions[] = [];
  filteredReportTypesList: ReportTypeWithVersions[] = [];
  monthlyDatesList: PriorMonthsList = [];
  formattedMonthlyDatesList: Array<{dateStr: string, formattedDate: string}> = [];
  quarterlyDatesList: PriorQuartersList = [];
  periodicityOptions = [
    { value: 'daily', label: 'Daily', id: 6 },
    { value: 'monthly', label: 'Monthly', id: 3 },
    { value: 'quarterly', label: 'Quarterly', id: 4 }
  ];
  versionsList: string[] = [
    "All",
    "Latest"
  ];

  currentOffset = 0;
  EXPORT_PAGE_LIMIT = 200;

  industryColumnDefs = [
    { headerName: 'Report Name', field: 'reportName', headerClass: 'header-wrap' },
    { headerName: 'Period', field: 'reportPeriodDate', headerClass: 'header-wrap' },
    { headerName: 'Version', field: 'reportDataVersion', headerClass: 'header-wrap' },
    { headerName: '# of generated firms', field: 'reportCount', headerClass: 'header-wrap' },
    { headerName: '# of firm reports viewed', field: 'reportViewedCount', headerClass: 'header-wrap' },
    { headerName: '# of firm reports not viewed', field: 'reportNotViewedCount', headerClass: 'header-wrap' },
    { headerName: '% of firm reports viewed', field: 'reportViewedPercent', headerClass: 'header-wrap' },
    { headerName: '# of summary report downloads', field: 'summaryDownloadCount', headerClass: 'header-wrap' },
    { headerName: '% of summary report downloads', field: 'summaryDownloadPercent', headerClass: 'header-wrap' },
    { headerName: '# of detail report downloads', field: 'detailDownloadCount', headerClass: 'header-wrap' },
    { headerName: '% of detail report downloads', field: 'detailDownloadPercent', headerClass: 'header-wrap' },
    { headerName: 'Mean Time between Publish and First View', field: 'publishViewAverageTime', headerClass: 'header-wrap' }
  ];

  firmColumnDefs = [
    { headerName: 'Report name', field: 'reportName', headerClass: 'header-wrap' },
    { headerName: 'Period', field: 'reportPeriodDate', headerClass: 'header-wrap' },
    { headerName: 'Version', field: 'reportDataVersion', headerClass: 'header-wrap' },
    { headerName: 'Firm Id', field: 'reportFirmId', headerClass: 'header-wrap' },
    { headerName: 'Distinct Report Summary Views', field: 'totalViews', headerClass: 'header-wrap' },
    { headerName: 'Distinct Report Summary Downloads', field: 'totalSummaryDownloads', headerClass: 'header-wrap' },
    { headerName: 'Distinct Report Detail Downloads', field: 'totalDetailDownloads', headerClass: 'header-wrap' },
    { headerName: 'Mean Time between Publish and First View', field: 'publishViewAverageTime', headerClass: 'header-wrap' },
    { headerName: 'Median Time between Publish and First View', field: 'publishViewMedianTime', headerClass: 'header-wrap' }
  ]

  industryRowData = [];
  firmRowData = [];

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private auditStatsService: AuditStatsService,
    private uiWidgetsService: UiWidgetsService,
  ) {}

  ngOnInit() {
    this.auditSearchFormGroup = this.fb.group({
      periodicity: ['', Validators.required],
      reports: [[], Validators.required],
      startDate: [''],
      endDate: [''],
      version: ['All'],
      firmId: ['']
    }, { validators: this.dateRangeValidator });

    // Listen to periodicity changes
    this.auditSearchFormGroup.get('periodicity').valueChanges.subscribe((periodicity) => {
      this.auditSearchFormGroup.patchValue({ startDate: '', endDate: '', reports: [] });
      const selectedPeriod = this.periodicityOptions.find(p => p.value === periodicity);
      if (selectedPeriod) {
        this.filteredReportTypesList = this.reportTypesList.filter(rt => rt.reportPeriodicityId === selectedPeriod.id);
      } else {
        this.filteredReportTypesList = [];
      }
    });

    // Listen to report changes
    this.auditSearchFormGroup.get('reports').valueChanges.subscribe((selectedReports) => {
      if (!this.auditSearchFormGroup.get('periodicity').value && selectedReports?.length > 0) {
        const firstSelectedReport = this.reportTypesList.find(rt => rt.reportTypeName === selectedReports[0]);
        if (firstSelectedReport) {
          const matchingPeriod = this.periodicityOptions.find(p => p.id === firstSelectedReport.reportPeriodicityId);
          if (matchingPeriod) {
            this.auditSearchFormGroup.patchValue({ periodicity: matchingPeriod.value }, { emitEvent: false });
            this.filteredReportTypesList = this.reportTypesList.filter(rt => rt.reportPeriodicityId === matchingPeriod.id);
          }
        }
      }


      if (selectedReports?.length === 1) {
        const selectedReport = this.reportTypesList.find(rt => rt.reportTypeName === selectedReports[0]);
        const versions = selectedReport?.versions || [];
        this.versionsList = ["All", "Latest", ...versions.sort((a, b) => a - b).map(v => v.toString())];
      } else {
        this.versionsList = ["All", "Latest"];
        this.auditSearchFormGroup.patchValue({ version: 'All' });
      }

    });

    this.adminService.getReportTypesWithVersions().subscribe({
      next: (reportTypesWithVersions: ReportTypeWithVersions[]) => {
        this.reportTypesList = deepSort(reportTypesWithVersions, 'reportTypeName', false, true);
        this.filteredReportTypesList = this.reportTypesList;
      }
    })


    const currentDate: string = convertDateToString(new Date(), 'yyyy-MM-dd');

    this.monthlyDatesList = getPrior36Months(currentDate, true);
    this.formattedMonthlyDatesList = this.monthlyDatesList.map(month => ({
      dateStr: month.dateStr,
      formattedDate: getMonthYrDate(month.dateStr)
    }));
    this.quarterlyDatesList = getPrior12Quarters(currentDate);
  }

  selectState(state) {
    this.currentView = state;
    this.showGrid = false;
    this.industryRowData = [];
    this.firmRowData = [];
  }

  prepareParams(includeOffsetLimit: boolean = false): any {
    const selectedReportNames = this.submittedFormValues.reports;
    const reportTypeIds = this.reportTypesList
      .filter(rt => selectedReportNames?.includes(rt.reportTypeName))
      .map(rt => rt.reportTypeId);

    var convertedStartDate = this.auditSearchFormGroup.value.startDate;
    var convertedEndDate = this.auditSearchFormGroup.value.endDate;
    if(this.submittedFormValues.periodicity === "quarterly") {
      convertedStartDate = convertQuarterYearToISO8601(convertedStartDate);
      convertedEndDate = convertQuarterYearToISO8601(convertedEndDate);
    } else if(this.submittedFormValues.periodicity === "monthly") {
      convertedStartDate = convertMonthNameYearToISO8601(convertedStartDate);
      convertedEndDate = convertMonthNameYearToISO8601(convertedEndDate);
    }

    const params: any = {
      reportTypeIds: reportTypeIds.join(','),
      periodStart: convertedStartDate,
      periodEnd: convertedEndDate,
      version: this.submittedFormValues.version
    };

    if (includeOffsetLimit) {
      params.offset = 0;
      params.limit = this.EXPORT_PAGE_LIMIT;
    } else {
      params.offset = this.currentOffset;
    }

    if(this.currentView.value === 'firm') {
      params.firmId = this.submittedFormValues.firmId.toUpperCase();
    }

    return params;
  }

  dateRangeValidator = (formGroup: FormGroup) => {
    const startDate = formGroup.get('startDate')?.value;
    const endDate = formGroup.get('endDate')?.value;
    const periodicity = formGroup.get('periodicity')?.value;
    const endDateControl = formGroup.get('endDate');

    if (!startDate || !endDate || !periodicity) {
      endDateControl?.setErrors(null);
      return null;
    }

    let startISO: string;
    let endISO: string;

    if (periodicity === 'quarterly') {
      startISO = convertQuarterYearToISO8601(startDate);
      endISO = convertQuarterYearToISO8601(endDate);
    } else if (periodicity === 'monthly') {
      startISO = convertMonthNameYearToISO8601(startDate);
      endISO = convertMonthNameYearToISO8601(endDate);
    } else {
      startISO = startDate;
      endISO = endDate;
    }

    const isInvalid = new Date(startISO) > new Date(endISO);
    if (isInvalid) {
      endDateControl?.setErrors({ dateRangeInvalid: true });
      return { dateRangeInvalid: true };
    } else {
      endDateControl?.setErrors(null);
      return null;
    }
  };

  onSubmit() {
    this.submittedFormValues = { ...this.auditSearchFormGroup.value };

    const params = this.prepareParams();
    params.limit = 10000;

    if(this.currentView.value === 'industry') {
      this.auditStatsService.getIndustryAuditStatsData(params).subscribe({
        next: (data) => {
          this.industryRowData = this.formatIndustryData(data.auditStatsInstances);

          this.showGrid = true;
        },
        error: (error) => this.handleAuditStatsError(error)
      });
    } else if(this.currentView.value === 'firm') {
      this.auditStatsService.getFirmAuditStatsData(params).subscribe({
        next: (data) => {
          this.firmRowData = this.formatFirmData(data.auditStatsReportLevelInstances);

          this.showGrid = true;
        },
        error: (error) => this.handleAuditStatsError(error)
      });
    } else {
      this.uiWidgetsService.showErrorSnackbar("Invalid state. Please select either 'Industry' or 'Firm'.");
    }
  }

  formatIndustryData(data: any[]): any[] {
    data.forEach((row) => {
      row.reportPeriodDate = trimDateTimeToDate(row.reportPeriodDate);
      if(this.submittedFormValues.periodicity === "quarterly") {
        row.reportPeriodDate = quarterDateFormat(row.reportPeriodDate);
      } else if(this.submittedFormValues.periodicity === "monthly") {
        row.reportPeriodDate = getMonthYrDate(row.reportPeriodDate);
      }

      row.reportViewedPercent = finraGridPercentValueFormatter({ value: row.reportViewedPercent });
      row.summaryDownloadPercent = finraGridPercentValueFormatter({ value: row.summaryDownloadPercent });
      row.detailDownloadPercent = finraGridPercentValueFormatter({ value: row.detailDownloadPercent });
      row.publishViewAverageTime = convertISO8601IntervalToPostgres(row.publishViewAverageTime);
    });
    return data;
  }

  formatFirmData(data: any[]): any[] {
    data.forEach((row) => {
      row.reportPeriodDate = trimDateTimeToDate(row.reportPeriodDate);
      if(this.submittedFormValues.periodicity === "quarterly") {
        row.reportPeriodDate = quarterDateFormat(row.reportPeriodDate);
      } else if(this.submittedFormValues.periodicity === "monthly") {
        row.reportPeriodDate = getMonthYrDate(row.reportPeriodDate);
      }

      row.publishViewAverageTime = convertISO8601IntervalToPostgres(row.publishViewAverageTime);
      row.publishViewMedianTime = convertISO8601IntervalToPostgres(row.publishViewMedianTime);
    });
    return data;
  }

  onExport() {
    const selectedReportNames = this.submittedFormValues.reports;
    let offset = 0;
    const allData = [];

    const fetchPage = () => {
      const params = this.prepareParams(true);
      params.offset = offset;

      if(this.currentView.value === 'industry') {
        this.auditStatsService.getIndustryAuditStatsData(params).subscribe({
          next: (data) => {
            const results = data.auditStatsInstances;
            allData.push(...results);

            if (results.length === this.EXPORT_PAGE_LIMIT) {
              offset += this.EXPORT_PAGE_LIMIT;
              fetchPage();
            } else {
              this.formatIndustryData(allData);

              const csvStringList: string[] = [];
              csvStringList.push('Report Name, Period, Version, # of generated firms, # of firm reports viewed, # of firm reports not viewed, % of firm reports viewed, # of summary report downloads, % of summary report downloads, # of detail report downloads, % of detail report downloads, Mean Time between Publish and First View');
              allData.forEach(row => {
                csvStringList.push(`${row.reportName}, ${row.reportPeriodDate}, ${row.reportDataVersion}, ${row.reportCount}, ${row.reportViewedCount}, ${row.reportNotViewedCount}, ${row.reportViewedPercent}, ${row.summaryDownloadCount}, ${row.summaryDownloadPercent}, ${row.detailDownloadCount}, ${row.detailDownloadPercent}, ${row.publishViewAverageTime}`);
              });
              const exportDataString: string = csvStringList.join('\n');
              const csvString = trimTrailingChar(exportDataString, ',');

              const filterInfo = `View: ${this.currentView.name}\nReports: ${selectedReportNames.join(', ')}\nStart Date: ${this.submittedFormValues.startDate}\nEnd Date: ${this.submittedFormValues.endDate}\nVersion: ${this.submittedFormValues.version}`;

              const now = new Date();
              const timestamp = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}_${String(now.getMinutes()).padStart(2, '0')}_${String(now.getSeconds()).padStart(2, '0')}`;

              const zip = new JSZip();
              zip.file('audit-stats-industry.csv', csvString);
              zip.file('filter-info.txt', filterInfo);
              zip.generateAsync({ type: 'blob' }).then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit_stats_industry_${timestamp}.zip`;
                a.click();
                window.URL.revokeObjectURL(url);
              });
            }
          },
          error: (error) => this.handleAuditStatsError(error)
        });
      } else if(this.currentView.value === 'firm') {
        this.auditStatsService.getFirmAuditStatsData(params).subscribe({
          next: (data) => {
            const results = data.auditStatsReportLevelInstances;
            allData.push(...results);

            if (results.length === this.EXPORT_PAGE_LIMIT) {
              offset += this.EXPORT_PAGE_LIMIT;
              fetchPage();
            } else {
              this.formatFirmData(allData);

              const csvStringList: string[] = [];
              csvStringList.push('Report Name, Period, Version, Firm Id, Distinct Report Summary Views, Distinct Report Summary Downloads, Distinct Report Detail Downloads, Mean Time between Publish and First View, Median Time between Publish and First View');
              allData.forEach(row => {
                csvStringList.push(`${row.reportName}, ${row.reportPeriodDate}, ${row.reportDataVersion}, ${row.reportFirmId}, ${row.totalViews}, ${row.totalSummaryDownloads}, ${row.totalDetailDownloads}, ${row.publishViewAverageTime}, ${row.publishViewMedianTime}`);
              });
              const exportDataString: string = csvStringList.join('\n');
              const csvString = trimTrailingChar(exportDataString, ',');

              const filterInfo = `View: ${this.currentView.name}\nReports: ${selectedReportNames.join(', ')}\nStart Date: ${this.submittedFormValues.startDate}\nEnd Date: ${this.submittedFormValues.endDate}\nVersion: ${this.submittedFormValues.version}\nFirm ID: ${this.submittedFormValues.firmId || 'N/A'}`;

              const now = new Date();
              const timestamp = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}_${String(now.getMinutes()).padStart(2, '0')}_${String(now.getSeconds()).padStart(2, '0')}`;

              const zip = new JSZip();
              zip.file('audit-stats-firm.csv', csvString);
              zip.file('filter-info.txt', filterInfo);
              zip.generateAsync({ type: 'blob' }).then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit_stats_firm_${timestamp}.zip`;
                a.click();
                window.URL.revokeObjectURL(url);
              });
            }
          },
          error: (error) => this.handleAuditStatsError(error)
        })
      }
    };

    fetchPage();
  }

  handleAuditStatsError(error: HttpErrorResponse) {
    if(error.status >= 500) {
      this.uiWidgetsService.showErrorSnackbar("Server error. Please try again later.");
    } else {
      switch(error.status) {
        case 400:
        case 405:
          this.uiWidgetsService.showErrorSnackbar("Invalid request parameters.");
          break;
        case 401:
          this.uiWidgetsService.showErrorSnackbar("Please log in and try again.");
          break;
        case 403:
          this.uiWidgetsService.showErrorSnackbar("You are not authorized to access Audit Stats data.");
          break;
      }
    }
  }
}
